import { getCuratedEventsForTheme } from './discoveryThemes';

export const HOME_FALLBACK_LOCATION = {
  lat: 36.95,
  lng: -122.1,
  label: 'Monterey Bay sample coast',
};

const HOME_COUNTDOWNS = {
  wildlife: [
    {
      id: 'grunion-run',
      title: 'Grunion run watch',
      subtitle: 'Warm spring surf nights',
      summary:
        'A public-friendly California marker for surf-zone wildlife timing, especially on calm evenings after strong moon-phase windows.',
      month: 4,
      day: 18,
      hour: 21,
      minute: 30,
      cadence: 'Estimated spring peak window',
      lat: 33.74,
      lon: -118.27,
      regionLabel: 'Southern California surf line',
    },
    {
      id: 'gray-whale-northbound',
      title: 'Gray whale northbound peak',
      subtitle: 'Coastal migration window',
      summary:
        'Useful when you want a broad migration signal that can turn an ordinary coastline stop into a real watch point.',
      month: 4,
      day: 27,
      hour: 9,
      minute: 0,
      cadence: 'Approximate peak passage',
      lat: 36.6,
      lon: -121.97,
      regionLabel: 'Monterey Bay migration watch',
    },
    {
      id: 'monarch-return',
      title: 'Monarch coastal return',
      subtitle: 'Late-season concentration watch',
      summary:
        'A reminder that some of the most legible wildlife moments are seasonal returns rather than one-night events.',
      month: 10,
      day: 25,
      hour: 11,
      minute: 0,
      cadence: 'Approximate autumn marker',
      lat: 36.62,
      lon: -121.94,
      regionLabel: 'Central California coastal groves',
    },
  ],
  celestial: [
    {
      id: 'perseids',
      title: 'Perseids peak',
      subtitle: 'Summer meteor shower window',
      summary:
        'One of the easiest public-facing sky events to explain and plan around, especially when moonlight is low and the sky stays dark late.',
      month: 8,
      day: 12,
      hour: 23,
      minute: 0,
      cadence: 'Annual peak night',
      lat: 37.87,
      lon: -119.38,
      regionLabel: 'Sierra dark-sky lane',
    },
    {
      id: 'milky-way-core',
      title: 'Milky Way core season',
      subtitle: 'Dark-sky planning marker',
      summary:
        'A strong seasonal anchor when the question is not just what is overhead, but when the sky feels most dramatic to casual observers.',
      month: 7,
      day: 15,
      hour: 22,
      minute: 0,
      cadence: 'Mid-season dark-sky window',
      lat: 37.24,
      lon: -118.78,
      regionLabel: 'Eastern Sierra night-sky corridor',
    },
    {
      id: 'geminids',
      title: 'Geminids peak',
      subtitle: 'Winter meteor watch',
      summary:
        'A late-year celestial draw that helps keep the front page alive outside the classic summer astronomy season.',
      month: 12,
      day: 13,
      hour: 23,
      minute: 0,
      cadence: 'Annual peak night',
      lat: 36.24,
      lon: -116.82,
      regionLabel: 'Mojave dark-sky basin',
    },
  ],
  earth: [
    {
      id: 'spring-upwelling',
      title: 'Spring upwelling pulse',
      subtitle: 'Cold nutrient-rich surfacing water',
      summary:
        'An Earth-systems marker that can change productivity, water color, wildlife context, and how a coastline feels on the surface.',
      month: 5,
      day: 20,
      hour: 8,
      minute: 0,
      cadence: 'Approximate seasonal onset',
      lat: 36.8,
      lon: -122.05,
      regionLabel: 'Monterey Bay shelf edge',
    },
    {
      id: 'summer-bloom-watch',
      title: 'Summer bloom watch',
      subtitle: 'Productivity pulse season',
      summary:
        'Useful as a reminder that some Earth events are less about a single day and more about a period when the system becomes visually and biologically expressive.',
      month: 7,
      day: 10,
      hour: 12,
      minute: 0,
      cadence: 'Seasonal watch window',
      lat: 47.61,
      lon: -122.44,
      regionLabel: 'Puget Sound bloom lane',
    },
    {
      id: 'king-tide-window',
      title: 'King tide window',
      subtitle: 'Extreme high-tide season',
      summary:
        'A strong public education moment because the event is visually legible and ties directly into long-term sea-level conversations.',
      month: 12,
      day: 10,
      hour: 7,
      minute: 30,
      cadence: 'Approximate winter window',
      lat: 37.81,
      lon: -122.48,
      regionLabel: 'Outer Bay shoreline',
    },
  ],
  cultural: [
    {
      id: 'hobiyee-window',
      title: 'Hobiyee watch',
      subtitle: 'Nisga’a New Year window',
      summary:
        'A strong lesser-known winter marker tied to the first crescent moon and the Nisga’a ceremonial calendar.',
      month: 2,
      day: 12,
      hour: 18,
      minute: 0,
      cadence: 'Approximate lunar-season window',
      lat: 55.09,
      lon: -129.98,
      regionLabel: 'Nisga’a homelands',
    },
    {
      id: 'first-salmon-window',
      title: 'First-salmon return window',
      subtitle: 'Species-linked seasonal practice',
      summary:
        'Useful for showing how a cultural calendar can be anchored to the return of a species, not only to a civic date.',
      month: 4,
      day: 20,
      hour: 10,
      minute: 0,
      cadence: 'Spring return-cycle watch',
      lat: 58.3,
      lon: -134.42,
      regionLabel: 'Northern Pacific salmon return coast',
    },
    {
      id: 'matariki',
      title: 'Matariki',
      subtitle: 'Maori New Year',
      summary:
        'A meaningful bridge between night-sky timing, remembrance, renewal, and planning for the coming season.',
      month: 7,
      day: 10,
      hour: 9,
      minute: 0,
      cadence: 'Annual midwinter observance',
      lat: -36.85,
      lon: 174.76,
      regionLabel: 'Aotearoa winter sky',
    },
  ],
};

const HOME_THEME_COORDINATES = {
  wildlife: {
    lat: 36.6,
    lon: -121.97,
    regionLabel: 'Central California migration coast',
  },
  celestial: {
    lat: 37.24,
    lon: -118.78,
    regionLabel: 'Eastern Sierra dark-sky lane',
  },
  earth: {
    lat: 36.8,
    lon: -122.05,
    regionLabel: 'California current edge',
  },
  cultural: {
    lat: 55.09,
    lon: -129.98,
    regionLabel: 'North Pacific cultural calendar lane',
  },
};

function normalizeCoordinate(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function readCoordinate(source, keys) {
  if (!source || typeof source !== 'object') return null;

  for (const key of keys) {
    const value = normalizeCoordinate(source[key]);
    if (value != null) return value;
  }

  return null;
}

export function resolveShowcaseCoordinate(item, theme) {
  const fallback = HOME_THEME_COORDINATES[theme] || HOME_THEME_COORDINATES.wildlife;
  const lat =
    readCoordinate(item, ['lat', 'latitude', 'center_lat', 'y']) ??
    readCoordinate(item?.location, ['lat', 'latitude']) ??
    fallback.lat;
  const lon =
    readCoordinate(item, ['lon', 'lng', 'longitude', 'center_lon', 'x']) ??
    readCoordinate(item?.location, ['lon', 'lng', 'longitude']) ??
    fallback.lon;

  return {
    lat,
    lon,
    regionLabel:
      item?.regionLabel ||
      item?.region_label ||
      item?.region ||
      item?.location_name ||
      item?.place_name ||
      fallback.regionLabel,
  };
}

export function formatCoordinate(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : 'TBD';
}

export function formatAtlasDate(value) {
  if (!value) return 'Timing TBD';
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function buildArchiveEventPayload(items) {
  const events = (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    title: item.title || item.name || item.event_type || 'Untitled event',
    summary:
      item.summary ||
      item.description ||
      item.reason ||
      'No summary available yet.',
    starts_at: item.starts_at || item.start_ts || item.start_date || '',
  }));

  return {
    summary: {
      event_count: events.length,
      active_count: events.filter((item) => item.status === 'active').length,
      upcoming_count: events.filter((item) => item.status !== 'active').length,
    },
    events,
  };
}

function nextAnnualMoment(item, referenceDate) {
  const current = new Date(referenceDate);
  const year = current.getUTCFullYear();

  const target = new Date(
    Date.UTC(year, item.month - 1, item.day, item.hour || 12, item.minute || 0, 0, 0)
  );

  if (target.getTime() <= current.getTime()) {
    target.setUTCFullYear(year + 1);
  }

  return target;
}

function describeCountdown(targetDate, referenceDate) {
  const diffMs = Math.max(0, targetDate.getTime() - referenceDate.getTime());
  const totalHours = Math.round(diffMs / 36e5);
  const totalDays = Math.floor(diffMs / 864e5);

  if (totalHours < 24) {
    return `${Math.max(1, totalHours)} hours`;
  }

  if (totalDays < 45) {
    return `${totalDays} days`;
  }

  const totalWeeks = Math.round(totalDays / 7);
  return `${totalWeeks} weeks`;
}

export function buildThemeCountdowns(theme, referenceDate = new Date()) {
  const items = HOME_COUNTDOWNS[theme] || [];

  return items.map((item) => {
    const targetDate = nextAnnualMoment(item, referenceDate);
    const coordinate = resolveShowcaseCoordinate(item, theme);

    return {
      ...item,
      isoDate: targetDate.toISOString(),
      countdownLabel: describeCountdown(targetDate, referenceDate),
      formattedDate: targetDate.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      }),
      ...coordinate,
    };
  });
}

export function buildHomeLeadFallback(theme, referenceDate = new Date()) {
  const countdown = buildThemeCountdowns(theme, referenceDate)[0];
  const curated = getCuratedEventsForTheme(theme)[0];

  if (curated) {
    const coordinate = resolveShowcaseCoordinate(curated, theme);

    return {
      title: curated.title,
      summary: curated.summary,
      status: curated.status || 'curated',
      starts_at: curated.starts_at,
      category_label: curated.category_label || 'Curated',
      distance_km: null,
      ...coordinate,
    };
  }

  if (countdown) {
    return {
      title: countdown.title,
      summary: countdown.summary,
      status: countdown.cadence,
      starts_at: countdown.isoDate,
      category_label: 'Seasonal marker',
      distance_km: null,
      lat: countdown.lat,
      lon: countdown.lon,
      regionLabel: countdown.regionLabel,
    };
  }

  return {
    title: 'Build your first local briefing',
    summary:
      'Share a location and Tellus will start organizing nearby signals, seasonal markers, and the next things worth paying attention to.',
    status: 'ready',
    starts_at: '',
    category_label: 'Tellus briefing',
    distance_km: null,
    lat: HOME_FALLBACK_LOCATION.lat,
    lon: HOME_FALLBACK_LOCATION.lng,
    regionLabel: HOME_FALLBACK_LOCATION.label,
  };
}
